import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NamedWidget, useFormApi } from 'lib-node-amb-renderer';

import ChakraAlert from '~/common/components/Alerts/Alert/Alert';
import AlertDescription from '~/common/components/Alerts/AlertDescription';
import AlertTitle from '~/common/components/Alerts/AlertTitle';
import Box from '~/common/components/Box';
import { formatCurrency } from '~/common/util/format';

export class AssetsAmountMismatchAlertClass extends NamedWidget {
  constructor(props) {
    super(props);
  }
}

type AssetsAmountMismatchAlertProps = {
  sx?: {
    [key: string]: any;
  };
  type: string;
};

//shows an alert if the two gift amount inputs do not match
const AssetsAmountMismatchAlert = ({
  sx,
  type,
}: Readonly<AssetsAmountMismatchAlertProps>) => {
  const { getState } = useFormApi();
  const { values } = getState();
  const docId = `1-${values?.documentId}`;
  const {
    statementDetails: {
      [docId]: {
        ['giftAmountInput']: giftAmountInput,
        ['giftAmountTransfer']: giftAmountTransfer,
        ['workflowEnhancementGiftAmountTransfer']:
          workflowEnhancementGiftAmountTransfer,
      },
    },
  } = values;

  const giftAmountInputValue = +giftAmountInput;
  const giftAmountTransferValue = +giftAmountTransfer;
  const workflowEnhancementGiftAmountTransferValue =
    +workflowEnhancementGiftAmountTransfer;
  const formattedGiftAmountTransfer = formatCurrency(giftAmountTransfer);
  const formattedWorkflowEnhancementGiftAmountTransfer = formatCurrency(
    workflowEnhancementGiftAmountTransfer,
  );
  let alertMessage = '';
  let showAmountMismatchAlert = false;
  let showAmountGreaterThanGiftAmount = false;

  //check if both gift amount inputs have values before comparing
  if (giftAmountInput && giftAmountTransfer) {
    showAmountMismatchAlert = giftAmountInputValue !== giftAmountTransferValue;
  }
  if (giftAmountInput && workflowEnhancementGiftAmountTransfer) {
    showAmountGreaterThanGiftAmount =
      workflowEnhancementGiftAmountTransferValue > giftAmountInputValue;
  }

  //set alert message text based on message prop
  switch (type) {
    case 'borrowerTransferMessage':
      alertMessage = showAmountMismatchAlert
        ? `Gift amount from the gift letter does not match the amount received by the borrower. Provide updated gift letter in the amount of ${formattedGiftAmountTransfer} that matches the amount transferred to the borrower.`
        : '';
      break;
    case 'titleTransferMessage':
      alertMessage = showAmountMismatchAlert
        ? `Gift amount from the gift letter does not match the amount received by title. Provide updated gift letter in the amount of ${formattedGiftAmountTransfer} that matches the amount transferred to title.`
        : '';
      break;
    case 'titleAtClosingTransferAmountGreaterThanGiftAmount':
      alertMessage = showAmountGreaterThanGiftAmount
        ? `Gift amount from the gift letter does not match the amount received at closing. Provide updated gift letter in the amount of ${formattedWorkflowEnhancementGiftAmountTransfer} that matches the amount transferred to title.`
        : '';
      break;
    case 'titlePriorToClosingTransferAmountGreaterThanGiftAmount':
      alertMessage = showAmountGreaterThanGiftAmount
        ? `Gift amount from the gift letter does not match the amount received by title. Provide updated gift letter in the amount of ${formattedWorkflowEnhancementGiftAmountTransfer} that matches the amount transferred to title.`
        : '';
      break;
    case 'borrowerTransferAmountGreaterThanGiftAmount':
      alertMessage = showAmountGreaterThanGiftAmount
        ? `Gift amount from the gift letter does not match the amount received by the borrower. Provide updated gift letter in the amount of ${formattedWorkflowEnhancementGiftAmountTransfer} that matches the amount transferred to the borrower.`
        : '';
      break;
    default:
      break;
  }
  const alertColor = 'orange.600';
  const alertBackground = 'orange.100';
  const alertIcon = faExclamationTriangle;
  const title = 'New Condition';

  return (
    <>
      {alertMessage && (
        <ChakraAlert
          border="1px solid"
          borderColor={alertColor}
          background={alertBackground}
          borderRadius="4px"
          w="100%"
          sx={sx}
          mt="1rem"
        >
          <Box color={`${alertColor}`} mr={2}>
            <FontAwesomeIcon icon={alertIcon} />
          </Box>

          <AlertTitle
            mr={4}
            fontSize="sm"
            color={alertColor}
            whiteSpace="nowrap"
          >
            {title}
          </AlertTitle>

          <AlertDescription fontSize="sm" lineHeight="24px">
            {alertMessage}
          </AlertDescription>
        </ChakraAlert>
      )}
    </>
  );
};

export default AssetsAmountMismatchAlert;
